/**
 * Maps technical WebAuthn/passkey errors to user-friendly messages.
 */
export function getPasskeyErrorMessage(error: unknown, context: 'login' | 'register' = 'login'): string {
  const message = typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message?: string }).message)
    : '';

  const lower = message.toLowerCase();

  // Timeout or "not allowed" (user cancelled, dismissed, or took too long)
  if (
    lower.includes('timed out') ||
    lower.includes('not allowed') ||
    lower.includes('w3.org') ||
    (lower.includes('webauthn') && (lower.includes('privacy') || lower.includes('considerations')))
  ) {
    return context === 'login'
      ? 'Passkey sign-in was cancelled or took too long. Please try again and use your passkey when prompted.'
      : 'Passkey setup was cancelled or took too long. Please try again.';
  }

  if (lower.includes('cancel') || lower.includes('abort') || lower.includes('notallowed')) {
    return context === 'login'
      ? 'Passkey sign-in was cancelled. Please try again when you\'re ready.'
      : 'Passkey setup was cancelled. You can add a passkey anytime from Security.';
  }

  if (lower.includes('not supported') || lower.includes('notsupported')) {
    return 'This device or browser doesn\'t support passkeys. Try another device or browser.';
  }

  if (lower.includes('invalid') || lower.includes('failed') || lower.includes('verification')) {
    return context === 'login'
      ? 'Passkey verification didn\'t succeed. Please try again or sign in with your password only on a device where you\'ve added a passkey.'
      : 'Passkey couldn\'t be verified. Please try again.';
  }

  if (lower.includes('security') || lower.includes('secure context')) {
    return 'Passkeys require a secure connection (HTTPS). If you\'re on a secure site, try again.';
  }

  return context === 'login'
    ? 'Passkey sign-in didn\'t complete. Please try again.'
    : 'Passkey setup didn\'t complete. Please try again.';
}
